import { useEffect, useState } from "react";
import { DeviceMobile, X, Export, ShareNetwork } from "@phosphor-icons/react";

/**
 * Install hint banner.
 * - Android / Desktop Chrome / Edge: catches `beforeinstallprompt` and shows an Install button
 * - iOS Safari: shows a one-time tutorial banner (Apple does not support beforeinstallprompt)
 * - Hides itself once installed or dismissed (remembered in localStorage)
 */
const DISMISS_KEY = "uptop-install-dismissed";

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (isStandalone()) return;
    try { if (localStorage.getItem(DISMISS_KEY) === "1") return; } catch (e) { /* ignore */ }

    setHidden(false);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS doesn't fire beforeinstallprompt — show the manual hint
    if (isIos()) setShowIos(true);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* ignore */ }
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") dismiss();
    setDeferred(null);
  };

  if (hidden || (!deferred && !showIos)) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md bg-[#161616] border border-[#27272A] shadow-2xl p-3 flex items-center gap-3 animate-in slide-in-from-bottom-4"
      data-testid="install-prompt"
    >
      <div className="w-10 h-10 flex items-center justify-center border border-[#E05A1D]/40 text-[#E05A1D] shrink-0 bg-[#E05A1D]/10">
        <DeviceMobile size={18} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold uppercase tracking-wide text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          Install UpTop
        </p>
        {deferred ? (
          <p className="text-xs text-[#A1A1AA]">Add to home screen for instant access.</p>
        ) : (
          <p className="text-xs text-[#A1A1AA] flex items-center gap-1.5">
            Tap <ShareNetwork size={14} weight="bold" className="text-[#E05A1D]" /> then <strong className="text-white">Add to Home Screen</strong>
          </p>
        )}
      </div>
      {deferred && (
        <button
          onClick={install}
          className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-3 py-2 text-xs flex items-center gap-1.5 shrink-0"
          data-testid="install-prompt-install-btn"
        >
          <Export size={14} weight="bold" /> Install
        </button>
      )}
      <button
        onClick={dismiss}
        className="text-[#71717A] hover:text-white transition-colors p-1 shrink-0"
        data-testid="install-prompt-dismiss-btn"
        aria-label="Dismiss"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
